import type React from "react";

import { styled } from "styled-components";

const DotPulseInner = styled.div<{
  $size: number;
  $color: string;
  $duration: number;
}>`
  --pulse-size: ${({ $size }) => $size}px;

  background-color: ${({ $color }) => $color};
  color: ${({ $color }) => $color};

  &::before,
  &::after {
    --pulse-size: ${({ $size }) => $size}px;
    background-color: ${({ $color }) => $color};
    color: ${({ $color }) => $color};
  }

  position: relative;

  animation: dotPulseAnimation ${({ $duration }) => $duration}s infinite
    ease-in-out;
  animation-delay: ${({ $duration }) => $duration / 6}s;

  top: ${({ $size }) => $size / 4}px;
  left: -9999px;
  width: var(--pulse-size);
  height: var(--pulse-size);
  border-radius: calc(var(--pulse-size) / 2);
  box-shadow: 9999px 0 0 calc(-1 * var(--pulse-size) / 2);

  &::before,
  &::after {
    content: "";
    display: inline-block;
    position: absolute;
    top: 0;
    width: var(--pulse-size);
    height: var(--pulse-size);
    border-radius: calc(var(--pulse-size) / 2);
  }

  &::before {
    box-shadow: calc(9999px - var(--pulse-size) * 1.5) 0 0
      calc(-1 * var(--pulse-size) / 2);
    animation: dotPulseBeforeAnimation ${({ $duration }) => $duration}s infinite
      ease-in-out;
    animation-delay: 0s;
  }

  &::after {
    box-shadow: calc(9999px + var(--pulse-size) * 1.5) 0 0
      calc(-1 * var(--pulse-size) / 2);
    animation: dotPulseAfterAnimation ${({ $duration }) => $duration}s infinite
      ease-in-out;
    animation-delay: ${({ $duration }) => $duration / 3}s;
  }

  @keyframes dotPulseBeforeAnimation {
    0% {
      box-shadow: calc(9999px - var(--pulse-size) * 1.5) 0 0
        calc(-1 * var(--pulse-size) / 2);
    }
    30% {
      box-shadow: calc(9999px - var(--pulse-size) * 1.5) 0 0
        calc(var(--pulse-size) / 5);
    }
    60%,
    100% {
      box-shadow: calc(9999px - var(--pulse-size) * 1.5) 0 0
        calc(-1 * var(--pulse-size) / 2);
    }
  }

  @keyframes dotPulseAnimation {
    0% {
      box-shadow: 9999px 0 0 calc(-1 * var(--pulse-size) / 2);
    }
    30% {
      box-shadow: 9999px 0 0 calc(var(--pulse-size) / 5);
    }
    60%,
    100% {
      box-shadow: 9999px 0 0 calc(-1 * var(--pulse-size) / 2);
    }
  }

  @keyframes dotPulseAfterAnimation {
    0% {
      box-shadow: calc(9999px + var(--pulse-size) * 1.5) 0 0
        calc(-1 * var(--pulse-size) / 2);
    }
    30% {
      box-shadow: calc(9999px + var(--pulse-size) * 1.5) 0 0
        calc(var(--pulse-size) / 5);
    }
    60%,
    100% {
      box-shadow: calc(9999px + var(--pulse-size) * 1.5) 0 0
        calc(-1 * var(--pulse-size) / 2);
    }
  }
`;

type DotPulseProps = {
  size?: number;
  duration?: number;
  style?: React.CSSProperties;
};

export const DotPulse = ({
  size = 10,
  duration = 1.5,
  style,
}: DotPulseProps) => {
  return (
    <div
      style={{
        ...(style ?? {}),
        position: "relative",
        height: size * 1.5,
        textAlign: "left",
        width: size * 5,
        display: "inline-block",
      }}
    >
      <DotPulseInner
        style={{ marginLeft: size * 2 }}
        $size={size}
        $color="#cacaca"
        $duration={duration}
      />
    </div>
  );
};
